// @noflow
import i18n from 'i18next';
// eslint-disable-next-line @spaced-out/i18n/no-react-i18next-import
import {initReactI18next} from 'react-i18next';
import Backend from 'i18next-chained-backend';
import LocalStorageBackend from 'i18next-localstorage-backend';
import HttpApi from 'i18next-http-backend';
import {isMissingTranslationsLoggingEnabled} from 'src/utils';

const localUntranslatedSet = new Set();

// in-memory fallback for language storage
let languageFallback = 'en';

// cache options for local storage backend
const cacheOptions = {
  enabled: true,
  prefix: 'sense-label-i18next-',
  expirationTime: 30 * 24 * 60 * 60 * 1000, // 30 days
  defaultVersion: 'v1.1',
  versions: {en: 'v1.1'},
  store: typeof window !== 'undefined' ? window.localStorage : null,
};

// Helper function to schedule work during idle time
const scheduleIdleWork = (callback) => {
  if (typeof window !== 'undefined' && window.requestIdleCallback) {
    // Use requestIdleCallback if available
    window.requestIdleCallback(callback, {timeout: 5000});
  } else {
    // Fallback to setTimeout for browsers without requestIdleCallback
    setTimeout(callback, 0);
  }
};

// function to construct API load path
const getLoadPath = (lng, ns) =>
  '/api/v1/configurations/label?lng=' + lng + '&ns=' + ns;

const httpApiOptions = {
  loadPath: getLoadPath('en', 'translations'),
  parse(data) {
    return JSON.parse(data);
  },
  requestOptions: {
    mode: 'cors',
    credentials: 'include',
    cache: 'default',
  },
};

// dynamically read available languages from local JSON files
const getAvailableLanguages = () => {
  try {
    const context = require.context('./translations', false, /\.json$/);

    const languages = new Set(
      context.keys().map((file) => {
        const match = file.match(/([^/]+)\.json$/);
        return match ? match[1] : null;
      }),
    );

    return Array.from(languages).filter(Boolean);
  } catch (error) {
    console.error('Error fetching available languages:', error);
    return [];
  }
};

// load translations for a specific language
const loadTranslations = async (language) => {
  try {
    const translationData = await import(`./translations/${language}.json`);

    let translations;
    if (
      typeof translationData === 'string' ||
      typeof translationData?.default === 'string'
    ) {
      // Case 1: Webpack resolved to a URL, fetch the JSON
      const response = await fetch(translationData.default || translationData);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch translation for ${language}: ${response.statusText}`,
        );
      }
      translations = await response.json();
    } else {
      // Case 2: Webpack provided a JSON object directly
      translations = translationData.default || translationData;
    }

    return translations;
  } catch (error) {
    console.error(`Error loading translation for ${language}:`, error);
    return null;
  }
};

// Create i18n instance
const labelI18nInstance = i18n.createInstance();

// Initialize i18n instance
let i18nInitialized = false;

export const initializeTranslations = async (
  agencyDefaultLang,
  enableMissingTranslationsLogs,
) => {
  if (i18nInitialized) {
    return;
  }

  const initialLanguage = (() => {
    // manually selected language (from localStorage)
    if (typeof localStorage !== 'undefined') {
      const storedLanguage = localStorage.getItem('selectedLanguage');
      if (storedLanguage) {
        return storedLanguage;
      }
    }
    return agencyDefaultLang || languageFallback;
  })();

  await labelI18nInstance
    .use(Backend)
    .use({
      type: 'languageDetector',
      async: false,
      init: () => {},
      detect: () => initialLanguage,
      cacheUserLanguage: (lng) => {
        // don't cache automatically - only cache when user manually selects
        languageFallback = lng;
      },
    })
    .use(initReactI18next)
    .init({
      debug: false,
      fallbackLng: 'en',
      backend: {
        backends: [
          LocalStorageBackend, // Local cache backend
          HttpApi, // API backend
        ],
        backendOptions: [cacheOptions, httpApiOptions],
      },
      resources: {},
      defaultNS: 'translation',
      ns: ['translation'],
      interpolation: {
        escapeValue: false,
      },
      react: {
        useSuspense: false,
        wait: false,
      },
      ...(enableMissingTranslationsLogs
        ? {
            saveMissing: true,
            missingKeyHandler(lng, ns, key, fallbackValue) {
              try {
                logUntranslated(key, fallbackValue);
              } catch (err) {
                console.error('missingKeyHandler error', err);
              }
            },
          }
        : {}),
    });

  i18nInitialized = true;
  // load the languages after i18n is initialized, & first load the default language
  const defaultLanguage = agencyDefaultLang || 'en';
  // default language is loaded first
  loadLanguageAndTranslationResourceBundle(defaultLanguage, true);
  // Load other languages during idle time to avoid blocking the main thread
  scheduleIdleWork(() => {
    loadOtherLanguages(defaultLanguage).catch((error) => {
      console.warn(
        'Failed to load some language resources during idle time:',
        error,
      );
    });
  });

  // change the language to the initial language
  if (labelI18nInstance.language !== initialLanguage) {
    await labelI18nInstance.changeLanguage(initialLanguage);
  }
};

const exists = (labelKey) => {
  try {
    // const lang = i18n.language || i18n.labelI18nInstance.language;
    const resources = labelI18nInstance?.store?.data?.['en']?.translation;
    if (resources) {
      return (
        resources && Object.prototype.hasOwnProperty.call(resources, labelKey)
      );
    }
    return 'NA';
  } catch (e) {
    return false;
  }
};

function logUntranslated(labelKey, labelValue) {
  try {
    if (!labelKey || typeof labelKey !== 'string' || exists(labelKey)) {
      return;
    }

    if (!localUntranslatedSet.has(labelKey)) {
      localUntranslatedSet.add(labelKey);

      const existing = JSON.parse(
        localStorage.getItem('untranslatedLabels') || '{}',
      );

      if (!existing[labelKey]) {
        existing[labelKey] = labelValue;
        localStorage.setItem('untranslatedLabels', JSON.stringify(existing));
      }
    }
  } catch (e) {
    console.error(e);
  }
}

export const loadLanguageAndTranslationResourceBundle = async (
  language,
  isDefaultLanguage = false,
) => {
  if (labelI18nInstance.hasResourceBundle(language, 'translation')) {
    return;
  }
  const translations = await loadTranslations(language);
  if (translations) {
    labelI18nInstance.addResourceBundle(language, 'translation', translations);
    if (isDefaultLanguage) {
      labelI18nInstance.changeLanguage(language);
    }
  }
};

export const loadOtherLanguages = async (defaultLanguage) => {
  const languages = getAvailableLanguages();
  // Load languages one by one during idle periods to avoid blocking
  for (const language of languages) {
    if (language !== defaultLanguage) {
      // Schedule each language load during idle time
      await new Promise((resolve) => {
        scheduleIdleWork(async () => {
          try {
            await loadLanguageAndTranslationResourceBundle(language);
          } catch (error) {
            console.warn(`Failed to load language ${language}:`, error);
          }
          resolve();
        });
      });
    }
  }
};

export const ensureInitialized = async (
  agencyLanguage,
  shouldLogMissingTranslations = true,
) => {
  const enableMissingTranslationsLogs =
    shouldLogMissingTranslations && isMissingTranslationsLoggingEnabled();
  if (!i18nInitialized) {
    await initializeTranslations(agencyLanguage, enableMissingTranslationsLogs);
  }
};

export default labelI18nInstance;
